import {DATE_FORMATS, LOG_LEVELS} from "constants/AppConstants.js";
import DateTimeUtil from "utils/DateTimeUtil.js";
import {writeStringToStorage} from "./FirebaseDbService.js";

let isAndroid = false;
let DiagnosticLogsPlugin: any = null;
let Network: any = null;
let App: any = null;
let Directory: any = null;
let Filesystem: any = null;

// Web/Android-only wiring, deliberately kept OFF the module top level. A
// top-level `await` here makes the whole ESM module async, and the Cloud
// Functions runtime (which require()s this shared package) then throws
// ERR_REQUIRE_ASYNC_MODULE on the async graph. On the server the guard below is
// false so this is a no-op; on web the dynamic imports resolve from the bundle
// before any log method is ever called.
void (async () => {
  if (typeof process.env.REACT_APP_FIREBASE_API_KEY === "undefined") {
    return;
  }
  // @ts-ignore -- web-bundler alias path, not resolvable by tsc
  const module = await import("/src/FirebaseUtils.js");
  isAndroid = module.isAndroid;
  if (!isAndroid) {
    return;
  }
  const moduleCapacitor = await import("@capacitor/core");
  const capacitorNetwork = await import("@capacitor/network");
  const capacitorApp = await import("@capacitor/app");
  const capacitorFilesystem = await import("@capacitor/filesystem");

  Directory = capacitorFilesystem.Directory;
  Filesystem = capacitorFilesystem.Filesystem;
  Network = capacitorNetwork.Network;
  App = capacitorApp.App;
  DiagnosticLogsPlugin = moduleCapacitor.registerPlugin("DiagnosticLogsPlugin");
})();

export class DbLog {
  d: string;
  l: string;
  m: any;
  st: any;

  constructor(date: string, level: string, message: any, stackTrace: any) {
    this.d = date;
    this.l = level;
    this.m = message;
    this.st = stackTrace;

    // Undefined properties are not saved in the database
    if (!stackTrace) {
      delete this.st;
    }
    if (!message) {
      delete this.m;
    }
  }
}

export class Log {
  nodeId: string;
  originalDate: string;
  date: Date;
  level: string;
  message: any;
  stackTrace: any;

  constructor(nodeId: string, date: string, level: string, message: any, stackTrace: any) {
    this.nodeId = nodeId;
    this.originalDate = date;
    this.date = DateTimeUtil.convertStringToDate(date);
    this.level = level;
    this.message = message;
    this.stackTrace = stackTrace;
  }
}

export default class LogService {
  static logCount: number = 0;

  static addInfoLogAsync(
    userId: string,
    message: any,
    skipBrowserLog: boolean = false,
    forced: boolean = false
  ): Promise<void> | void {
    if (!skipBrowserLog) {
      console.info(message);
    }

    if (isAndroid && forced) {
      // Convert message to string if it is a JSON
      if (typeof message === "object") {
        message = JSON.stringify(message);
      }
      DiagnosticLogsPlugin.pushLog({message});
      LogService.logCount++;
      return LogService.pushAndroidLogs(userId);
    }
  }

  static addErrorLogAsync(
    userId: string,
    message: any,
    stackTrace: any,
    skipBrowserLog: boolean = false,
    forced: boolean = false
  ): Promise<void> | void {
    if (!skipBrowserLog) {
      console.error({message: message, stackTrace: stackTrace});
    }

    if (isAndroid && forced) {
      // Convert message or stackTrace to string if they are JSON
      if (typeof message === "object") {
        message = JSON.stringify(message);
      }
      if (typeof stackTrace === "object") {
        stackTrace = JSON.stringify(stackTrace);
      }
      DiagnosticLogsPlugin.pushLog({message: `${message}\n${stackTrace}`});
      LogService.logCount++;
      return LogService.pushAndroidLogs(userId);
    }

    const date = DateTimeUtil.convertDateToString(new Date(), DATE_FORMATS.YYYYMMDDHHMMSSPlus0000);
    const log = new DbLog(date, LOG_LEVELS.error, message, stackTrace);

    LogService.addGenericErrorLogAsync(`User Log ${log} USER ${userId}`, "", true);
  }

  static addGenericErrorLogAsync(
    message: any,
    stackTrace: any,
    skipBrowserLog: boolean = false,
    forced: boolean = false
  ): Promise<any> {
    if (!skipBrowserLog) {
      console.error({message: message, stackTrace: stackTrace});
    }

    if (isAndroid && forced) {
      // Convert message or stackTrace to string if they are JSON
      if (typeof message === "object") {
        message = JSON.stringify(message);
      }
      if (typeof stackTrace === "object") {
        stackTrace = JSON.stringify(stackTrace);
      }
      return DiagnosticLogsPlugin.pushLog({message: `${message}\n${stackTrace}`});
    }

    return Promise.resolve();
  }

  static async pushAndroidLogs(userId: string, force: boolean = false): Promise<void> {
    if (isAndroid) {
      // Skip the expensive Capacitor bridge calls (Network/App state) when we
      // have nothing to flush. Otherwise this method gets called on every log
      // entry and stalls the WebView thread during high-volume operations
      // (e.g. cloud sync comparing hundreds of activities).
      if (!force && LogService.logCount <= 100) {
        return;
      }
      if (!userId) {
        return;
      }
      const status = await Network.getStatus();
      const {isActive} = await App.getState();
      if ((force || LogService.logCount > 100) && userId && status.connected && isActive) {
        try {
          const result = await DiagnosticLogsPlugin.readLogs();

          if (!result.path) {
            return;
          }

          const fileContents = await Filesystem.readFile({
            path: result.path,
            directory: Directory.Data,
            encoding: "utf8"
          });

          const lines = fileContents.data.split("\n");
          const lastLines = lines.slice(-5000).join("\n");

          LogService.logCount = 0;

          if (userId) {
            await writeStringToStorage(`sync/${userId}/WaterSpeedAndroid.log.txt`, lastLines);
          }
        } catch (error) {
          console.error("Failed to upload logs to Firebase Storage:", error);
        }
      }
    }
  }

  static async pushFileToCloudStorage(userId: string, path: string): Promise<void> {
    if (isAndroid) {
      const status = await Network.getStatus();
      const {isActive} = await App.getState();
      if (userId && status.connected && isActive) {
        try {
          const fileContents = await Filesystem.readFile({
            path: path,
            directory: Directory.Data,
            encoding: "utf8"
          });

          if (userId) {
            await writeStringToStorage(`sync/${userId}/ActivitySyncFailed.log.txt`, fileContents.data);
          }
        } catch (error) {
          console.error("Failed to upload logs to Firebase Storage:", error);
          throw error;
        }
      }
    }
  }

  static initializeAppStateListener(userId: string): any {
    if (isAndroid) {
      return App.addListener("resume", async () => {
        try {
          // Force push logs when app comes to foreground
          await LogService.pushAndroidLogs(userId, true);
        } catch (error) {
          console.error("Failed to push logs on resume:", error);
        }
      });
    }
  }
}
